import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useUser } from '@/contexts/UserContext';
import { useCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useWarehouses';
import { useTrips } from '@/hooks/useTrips';
import { useShipments } from '@/hooks/useShipments';
import { useInvoices } from '@/hooks/useInvoices';
import { useSourcingRequests } from '@/hooks/useSourcingRequests';
import { QRScanner } from './QRScanner';
import { MultiItemShipmentForm } from '@/components/shipments/MultiItemShipmentForm';
import { ArrowLeft, Package, FileText, CheckCircle, Clock, AlertCircle, Scan, Edit, X } from 'lucide-react';

interface Shipment {
  shipmentNumber: string;
  customerName: string;
  customerCode: string;
  customerEmail?: string;
  originWarehouse: string;
  destinationWarehouse: string;
  freightMethod?: 'Air' | 'Sea';
  status: 'Processing' | 'In Transit' | 'At Destination' | 'Delivered';
  weight: number;
  volume?: number;
  packages: number;
  tripName?: string;
  description?: string;
}

interface InvoiceShipment {
  shipmentNumber: string;
  description: string;
  weight: number;
  volume: number;
  packages: number;
  freightMethod: 'Air' | 'Sea';
  rate: number;
  amount: number;
}

interface Invoice {
  invoiceNumber: string;
  customerName: string;
  customerCode: string;
  customerEmail?: string;
  status: 'Draft' | 'Sent' | 'Paid' | 'Overdue' | 'Partial';
  total: number;
  issueDate: string;
  dueDate: string;
  tripName?: string;
  shipments: InvoiceShipment[];
}

/**
 * QR Scan Page Component
 * 
 * Dedicated page for scanning QR codes and updating status.
 * Uses backend API to fetch and update shipments/invoices.
 * 
 * To remove QR functionality:
 * 1. Delete src/components/qr/ directory
 * 2. Remove this route from AppRouter.tsx
 * 3. Remove Scan button from navigation
 */
export default function ScanPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { customers: apiCustomers } = useCustomers();
  const { warehouses: apiWarehouses } = useWarehouses();
  const { trips: apiTrips } = useTrips();
  const { updateShipment } = useShipments();
  const { updateInvoice } = useInvoices();
  const { updateSourcingRequest } = useSourcingRequests();
  
  const customers = apiCustomers || [];
  const warehouses = apiWarehouses || [];
  const trips = apiTrips || [];
  
  const [scanResult, setScanResult] = useState<{ type: 'shipment' | 'invoice' | 'request'; id: string } | null>(null);
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [request, setRequest] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(true);
  
  // Edit mode states
  const [isEditingShipment, setIsEditingShipment] = useState(false);
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [isEditingRequest, setIsEditingRequest] = useState(false);
  
  // Editing data states
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [editingRequest, setEditingRequest] = useState<any | null>(null);

  const API_BASE_URL = 'https://sourcemygoods.com/api/controllers';
  const API_PUBLIC_URL = 'https://sourcemygoods.com/api/public';

  // Handle QR scan result
  const handleScan = async (result: { type: 'shipment' | 'invoice'; id: string }) => {
    setScanResult(result);
    setLoading(true);
    setError(null);

    // Check if user is logged in
    if (!user || !user.token) {
      setError('Please login to use the scanner');
      setLoading(false);
      return;
    }

    try {
      if (result.type === 'shipment') {
        // Fetch shipment from API
        const response = await fetch(
          `${API_BASE_URL}/shipments.php?shipment_number=${encodeURIComponent(result.id)}`,
          {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          setError(data.message || `Shipment ${result.id} not found`);
          setShipment(null);
          return;
        }

        // Format data for display
        const shipmentData: Shipment = {
          shipmentNumber: data.data.shipment_number || data.data.shipmentNumber,
          customerName: data.data.customer_name || data.data.customerName,
          customerCode: data.data.customer_code || data.data.customerCode,
          customerEmail: data.data.customer_email || data.data.customerEmail,
          originWarehouse: data.data.origin_warehouse || data.data.originWarehouse || '',
          destinationWarehouse: data.data.destination_warehouse || data.data.destinationWarehouse,
          freightMethod: data.data.freight_method || data.data.freightMethod || 'Air',
          status: data.data.status,
          weight: parseFloat(data.data.weight) || 0,
          volume: parseFloat(data.data.volume) || 0,
          packages: parseInt(data.data.packages) || 0,
          tripName: data.data.trip_name || data.data.tripName || null,
          description: data.data.description || ''
        };

        // Check warehouse user permissions
        if (user?.role === 'warehouse') {
          const canAccess = 
            shipmentData.destinationWarehouse === user.warehouseCode ||
            shipmentData.originWarehouse === user.warehouseCode;

          if (!canAccess) {
            setError('Access denied: Shipment not assigned to your warehouse');
            setShipment(null);
            return;
          }
        }

        setShipment(shipmentData);
        setInvoice(null);

      } else if (result.type === 'invoice') {
        // Fetch invoice from API
        const response = await fetch(
          `${API_BASE_URL}/invoices.php?invoice_number=${encodeURIComponent(result.id)}`,
          {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          setError(data.message || `Invoice ${result.id} not found`);
          setInvoice(null);
          return;
        }

        // Format shipments for display
        const rate = parseFloat(data.data.rate_per_kg) || 0;
        const shipmentsData = (data.data.shipments || []).map((s: any) => {
          const freightMethod = s.freightMethod || s.freight_method || 'Air';
          const weight = parseFloat(s.weight) || 0;
          const volume = parseFloat(s.volume) || 0;
          const amount = freightMethod === 'Air' ? (weight * rate) : (volume * rate);
          
          return {
            shipmentNumber: s.shipmentNumber || s.shipment_number,
            description: s.description || 'Shipment',
            weight: weight,
            volume: volume,
            packages: parseInt(s.packages) || 0,
            freightMethod: freightMethod,
            rate: rate,
            amount: amount
          };
        });

        // Format data for display
        const invoiceData: Invoice = {
          invoiceNumber: data.data.invoice_number || data.data.invoiceNumber,
          customerName: data.data.customer_name || data.data.customerName,
          customerCode: data.data.customer_code || data.data.customerCode,
          customerEmail: data.data.customer_email || data.data.customerEmail,
          status: data.data.status,
          total: parseFloat(data.data.total) || 0,
          issueDate: data.data.issue_date || data.data.issueDate,
          dueDate: data.data.due_date || data.data.dueDate,
          tripName: data.data.trip_name || data.data.tripName || '',
          shipments: shipmentsData
        };

        setInvoice(invoiceData);
        setShipment(null);
        setRequest(null);

      } else if (result.type === 'request') {
        // Fetch sourcing request from API
        const response = await fetch(
          `${API_BASE_URL}/sourcing-requests.php?request_number=${encodeURIComponent(result.id)}`,
          {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          setError(data.message || `Sourcing request ${result.id} not found`);
          setRequest(null);
          return;
        }

        // Format data for display
        const requestData = {
          ...data.data,
          photos: typeof data.data.photos === 'string' ? JSON.parse(data.data.photos || '[]') : (data.data.photos || []),
          items: typeof data.data.items === 'string' ? JSON.parse(data.data.items || '[]') : (data.data.items || []),
          referenceLinks: typeof data.data.reference_links === 'string' ? JSON.parse(data.data.reference_links || '[]') : (data.data.reference_links || [])
        };

        setRequest(requestData);
        setShipment(null);
        setInvoice(null);
      }
    } catch (err) {
      setError('Error loading data. Please try again.');
      console.error('Scan error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Update shipment status
  const updateShipmentStatus = async (newStatus: string) => {
    if (!shipment || !user || !user.token) return;

    setUpdating(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/shipments.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          shipment_number: shipment.shipmentNumber,
          status: newStatus
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to update shipment status');
        return;
      }

      // Update local state
      setShipment({ ...shipment, status: newStatus as Shipment['status'] });

      // Send email notification to customer (silently - don't block on errors)
      if (shipment.customerEmail) {
        fetch(`${API_PUBLIC_URL}/send-shipment-status-email.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            to: shipment.customerEmail,
            shipmentNumber: shipment.shipmentNumber,
            newStatus: newStatus,
            customerName: shipment.customerName,
            freightMethod: shipment.freightMethod || 'Air',
            origin: shipment.originWarehouse,
            destination: shipment.destinationWarehouse,
            weight: shipment.weight || 0,
            volume: shipment.volume || 0,
            packages: shipment.packages || 0,
            description: shipment.description || '',
            tripName: shipment.tripName || ''
          })
        }).catch(err => console.error('Email error:', err));
      }
      
      // Auto-clear after success
      setTimeout(() => {
        setScanResult(null);
        setShipment(null);
      }, 2000);

    } catch (err) {
      setError('Error updating status. Please try again.');
      console.error('Update error:', err);
    } finally {
      setUpdating(false);
    }
  };

  // Update invoice status
  const updateInvoiceStatus = async (newStatus: string) => {
    if (!invoice || !user || !user.token) return;

    setUpdating(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/invoices.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          invoice_number: invoice.invoiceNumber,
          status: newStatus,
          ...(newStatus === 'Paid' && { 
            paid_date: new Date().toISOString().split('T')[0],
            amount_paid: invoice.total,
            balance_due: 0
          })
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to update invoice status');
        return;
      }

      // Update local state
      setInvoice({ ...invoice, status: newStatus as Invoice['status'] });

      // Send email notification to customer (same logic as useInvoices hook)
      try {
        const customer = customers.find((c: any) => 
          c.code === invoice.customerCode
        );

        if (customer && customer.email) {
          await fetch(`${API_PUBLIC_URL}/send-invoice-status-email.php`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              to: customer.email,
              customerName: customer.name || 'Valued Customer',
              invoiceNumber: invoice.invoiceNumber,
              newStatus: newStatus,
              totalAmount: invoice.total,
              balance: newStatus === 'Paid' ? 0 : invoice.total,
              dueDate: invoice.dueDate,
              issueDate: invoice.issueDate,
              shipments: invoice.shipments || [],
              tripName: invoice.tripName || ''
            })
          });
        }
      } catch (emailErr) {
        console.error('Email notification error:', emailErr);
        // Don't fail the operation if email fails
      }
      
      // Auto-clear after success
      setTimeout(() => {
        setScanResult(null);
        setInvoice(null);
      }, 2000);

    } catch (err) {
      setError('Error updating status. Please try again.');
      console.error('Update error:', err);
    } finally {
      setUpdating(false);
    }
  };

  // Status color helper
  const getStatusColor = (status: string) => {
    switch (status) {
      // Shipment/Invoice statuses
      case 'Delivered':
      case 'Paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'At Destination':
      case 'Sent': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Processing':
      case 'Draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'In Transit': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Overdue': return 'bg-red-100 text-red-800 border-red-200';
      
      // Sourcing request statuses
      case 'Pending': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'Under Review': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Quoted': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Waiting for Payment': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Payment Received': return 'bg-green-100 text-green-800 border-green-200';
      case 'Order Placed with Supplier': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'In Production': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Ready for Shipment': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'Cancelled': return 'bg-red-100 text-red-800 border-red-200';
      
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Reset scan
  const resetScan = () => {
    setScanResult(null);
    setShipment(null);
    setInvoice(null);
    setRequest(null);
    setError(null);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">QR Scanner</h1>
            <p className="text-gray-600">Scan QR codes to view shipments, invoices, or sourcing requests</p>
          </div>
          <Button variant="outline" onClick={resetScan}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Reset
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Scanner Section */}
          <div className="space-y-4">
            <QRScanner onScan={handleScan} onError={setError} />
            
            {/* Loading State */}
            {loading && (
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Loading...</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Result Section */}
          <div className="space-y-4">
            {error && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Shipment Result */}
            {shipment && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Shipment: {shipment.shipmentNumber}
                  </CardTitle>
                  <CardDescription>
                    Update shipment status
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Customer:</span>
                      <p>{shipment.customerName} ({shipment.customerCode})</p>
                    </div>
                    <div>
                      <span className="font-medium">Route:</span>
                      <p>{shipment.originWarehouse || 'N/A'} → {shipment.destinationWarehouse}</p>
                    </div>
                    <div>
                      <span className="font-medium">Trip:</span>
                      <p>{shipment.tripName || 'Not Assigned'}</p>
                    </div>
                    <div>
                      <span className="font-medium">Freight Method:</span>
                      <p>
                        {shipment.freightMethod === 'Air' ? '✈️ Air Freight' : '🚢 Sea Freight'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium">Weight:</span>
                      <p>{shipment.weight} kg</p>
                    </div>
                    {shipment.freightMethod === 'Sea' && shipment.volume && (
                      <div>
                        <span className="font-medium">Volume:</span>
                        <p>{shipment.volume.toFixed(2)} m³</p>
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Packages:</span>
                      <p>{shipment.packages}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="font-medium text-sm">Current Status:</span>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(shipment.status)}>
                        {shipment.status}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="font-medium text-sm">Update Status:</span>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateShipmentStatus('Processing')}
                        disabled={shipment.status === 'Processing' || updating}
                      >
                        Processing
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateShipmentStatus('In Transit')}
                        disabled={shipment.status === 'In Transit' || updating}
                      >
                        In Transit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateShipmentStatus('At Destination')}
                        disabled={shipment.status === 'At Destination' || updating}
                      >
                        At Destination
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateShipmentStatus('Delivered')}
                        disabled={shipment.status === 'Delivered' || updating}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {updating ? 'Updating...' : 'Delivered'}
                      </Button>
                    </div>
                  </div>

                  {/* Edit Button - Admin and Warehouse only */}
                  {user && (user.role === 'admin' || user.role === 'warehouse') && (
                    <div className="pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setIsEditingShipment(true)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Shipment Details
                    </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Inline Edit Shipment Form */}
            {isEditingShipment && shipment && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Edit Shipment: {shipment.shipmentNumber}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingShipment(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    Update shipment information and item details
                  </CardDescription>
                </CardHeader>
                <CardContent className="max-h-[70vh] overflow-y-auto">
                  <MultiItemShipmentForm
                    customers={customers || []}
                    trips={trips || []}
                    warehouses={warehouses || []}
                    initialData={shipment}
                    onSubmit={(data) => {
                      updateShipment({
                        shipment_number: shipment.shipmentNumber,
                        ...data
                      });
                      setIsEditingShipment(false);
                      setShowDetails(false);
                      // Refresh the page after a short delay
                      setTimeout(() => {
                        window.location.reload();
                      }, 500);
                    }}
                    isSubmitting={false}
                    showTripSelection={true}
                    defaultStatus={shipment.status}
                  />
                </CardContent>
              </Card>
            )}

            {/* Invoice Result */}
            {invoice && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Invoice: {invoice.invoiceNumber}
                  </CardTitle>
                  <CardDescription>
                    Update invoice status
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Customer:</span>
                      <p>{invoice.customerName} ({invoice.customerCode})</p>
                    </div>
                    <div>
                      <span className="font-medium">Total Amount:</span>
                      <p>${invoice.total.toFixed(2)}</p>
                    </div>
                    <div>
                      <span className="font-medium">Issue Date:</span>
                      <p>{invoice.issueDate}</p>
                    </div>
                    <div>
                      <span className="font-medium">Due Date:</span>
                      <p>{invoice.dueDate}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="font-medium text-sm">Current Status:</span>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(invoice.status)}>
                        {invoice.status}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="font-medium text-sm">Update Status:</span>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateInvoiceStatus('Draft')}
                        disabled={invoice.status === 'Draft' || updating}
                      >
                        Draft
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateInvoiceStatus('Sent')}
                        disabled={invoice.status === 'Sent' || updating}
                      >
                        Sent
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateInvoiceStatus('Paid')}
                        disabled={invoice.status === 'Paid' || updating}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {updating ? 'Updating...' : 'Paid'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateInvoiceStatus('Overdue')}
                        disabled={invoice.status === 'Overdue' || updating}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Overdue
                      </Button>
                    </div>
                  </div>

                  {/* Edit Button */}
                  <div className="pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setEditingInvoice(invoice);
                        setIsEditingInvoice(true);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Invoice Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Inline Edit Invoice Form */}
            {isEditingInvoice && editingInvoice && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Edit Invoice: {editingInvoice.invoiceNumber}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsEditingInvoice(false);
                        setEditingInvoice(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    Update invoice details and status
                  </CardDescription>
                </CardHeader>
                <CardContent className="max-h-[70vh] overflow-y-auto space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="editInvoiceNumber" className="text-sm font-medium">Invoice Number</Label>
                    <Input
                      id="editInvoiceNumber"
                      value={editingInvoice.invoiceNumber}
                      className="bg-gray-50"
                      readOnly
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="editCustomerCode" className="text-sm font-medium">Customer Code</Label>
                      <Input
                        id="editCustomerCode"
                        value={editingInvoice.customerCode}
                        onChange={(e) => setEditingInvoice({...editingInvoice, customerCode: e.target.value})}
                        maxLength={4}
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editStatus" className="text-sm font-medium">Status</Label>
                      <Select
                        value={editingInvoice.status}
                        onValueChange={(value: Invoice['status']) => setEditingInvoice({...editingInvoice, status: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Draft">Draft</SelectItem>
                          <SelectItem value="Sent">Sent</SelectItem>
                          <SelectItem value="Paid">Paid</SelectItem>
                          <SelectItem value="Overdue">Overdue</SelectItem>
                          <SelectItem value="Partial">Partial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="editCustomerName" className="text-sm font-medium">Customer Name</Label>
                    <Input
                      id="editCustomerName"
                      value={editingInvoice.customerName}
                      onChange={(e) => setEditingInvoice({...editingInvoice, customerName: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="editCustomerPhone" className="text-sm font-medium">Customer Phone</Label>
                    <Input
                      id="editCustomerPhone"
                      type="tel"
                      value={editingInvoice.customerPhone || ''}
                      onChange={(e) => setEditingInvoice({...editingInvoice, customerPhone: e.target.value || undefined})}
                      placeholder="+218 912345678"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="editTripName" className="text-sm font-medium">Trip Name</Label>
                    <Input
                      id="editTripName"
                      value={editingInvoice.tripName || ''}
                      onChange={(e) => setEditingInvoice({...editingInvoice, tripName: e.target.value || undefined})}
                      placeholder="Optional trip name"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="editIssueDate" className="text-sm font-medium">Issue Date</Label>
                      <Input
                        id="editIssueDate"
                        type="date"
                        value={editingInvoice.issueDate}
                        onChange={(e) => setEditingInvoice({...editingInvoice, issueDate: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editDueDate" className="text-sm font-medium">Due Date</Label>
                      <Input
                        id="editDueDate"
                        type="date"
                        value={editingInvoice.dueDate}
                        onChange={(e) => setEditingInvoice({...editingInvoice, dueDate: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="editRate" className="text-sm font-medium">Rate per kg ($)</Label>
                      <Input
                        id="editRate"
                        type="number"
                        step="0.01"
                        value={editingInvoice.shipments[0]?.rate || 0}
                        onChange={(e) => {
                          const newRate = parseFloat(e.target.value) || 0;
                          const updatedShipments = editingInvoice.shipments.map(shipment => ({
                            ...shipment,
                            rate: newRate,
                            amount: shipment.weight * newRate
                          }));
                          const subtotal = updatedShipments.reduce((sum, s) => sum + s.amount, 0);

                          setEditingInvoice({
                            ...editingInvoice,
                            shipments: updatedShipments,
                            subtotal,
                            total: subtotal - (editingInvoice.tax || 0)
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editDiscount" className="text-sm font-medium">Discount ($)</Label>
                      <Input
                        id="editDiscount"
                        type="number"
                        step="0.01"
                        value={editingInvoice.tax || 0}
                        onChange={(e) => {
                          const newDiscount = parseFloat(e.target.value) || 0;

                          setEditingInvoice({
                            ...editingInvoice,
                            tax: newDiscount,
                            total: editingInvoice.subtotal - newDiscount
                          });
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="amountPaid" className="text-sm font-medium">Amount Paid ($)</Label>
                    <Input
                      id="amountPaid"
                      type="number"
                      step="0.01"
                      value={editingInvoice.amountPaid || 0}
                      onChange={(e) => {
                        const paid = parseFloat(e.target.value) || 0;
                        const balance = editingInvoice.total - paid;

                        setEditingInvoice({
                          ...editingInvoice,
                          amountPaid: paid,
                          balance: balance,
                          balanceDue: balance,
                          status: balance === 0 ? 'Paid' : balance < editingInvoice.total ? 'Partial' : editingInvoice.status
                        });
                      }}
                    />
                    <p className="text-xs text-gray-500">
                      Enter partial or full payment amount
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Shipments ({editingInvoice.shipments.length})</Label>
                    <div className="border rounded-lg p-3 space-y-2 max-h-[240px] overflow-y-auto bg-gray-50">
                      {editingInvoice.shipments.map((shipment, index) => {
                        const isSeaFreight = shipment.freightMethod === 'Sea';
                        return (
                          <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 bg-white rounded shadow-sm border">
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              <Package className="h-4 w-4 text-blue-600 flex-shrink-0" />
                              <span className="font-semibold text-gray-900 truncate">{shipment.shipmentNumber}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                              <span className="whitespace-nowrap">
                                {isSeaFreight ? `${(shipment.volume || 0).toFixed(2)} CBM` : `${shipment.weight} kg`}
                              </span>
                              <span className="whitespace-nowrap">{shipment.packages} pkgs</span>
                              <span className="whitespace-nowrap">@ ${(shipment.rate || 0).toFixed(2)}/{isSeaFreight ? 'CBM' : 'kg'}</span>
                              <span className="font-semibold text-gray-900 whitespace-nowrap">${(shipment.amount || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-3 border-t pt-4 bg-blue-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">Subtotal:</span>
                      <span className="font-semibold text-gray-900 text-lg">${(editingInvoice.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {editingInvoice.tax > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Discount:</span>
                        <span className="font-semibold text-green-600">-${(editingInvoice.tax || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center border-t border-blue-200 pt-3">
                      <span className="text-base font-bold text-gray-900">Total Amount:</span>
                      <span className="text-2xl font-bold text-blue-600">${(editingInvoice.total || 0).toFixed(2)}</span>
                    </div>
                    {editingInvoice.amountPaid > 0 && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-700">Amount Paid:</span>
                          <span className="font-semibold text-green-600">${(editingInvoice.amountPaid || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center border-t border-blue-200 pt-2">
                          <span className="text-sm font-bold text-gray-900">Balance Due:</span>
                          <span className="text-lg font-bold text-orange-600">${(editingInvoice.balance || 0).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditingInvoice(false);
                        setEditingInvoice(null);
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        updateInvoice({
                          invoice_number: editingInvoice.invoiceNumber,
                          customer_code: editingInvoice.customerCode,
                          customer_name: editingInvoice.customerName,
                          customer_phone: editingInvoice.customerPhone,
                          trip_name: editingInvoice.tripName,
                          issue_date: editingInvoice.issueDate,
                          due_date: editingInvoice.dueDate,
                          status: editingInvoice.status,
                          shipments: editingInvoice.shipments.map(s => ({
                            shipment_number: s.shipmentNumber,
                            weight: s.weight,
                            volume: s.volume,
                            packages: s.packages,
                            rate: s.rate,
                            amount: s.amount,
                            freight_method: s.freightMethod
                          })),
                          subtotal: editingInvoice.subtotal,
                          tax: editingInvoice.tax,
                          total: editingInvoice.total,
                          amount_paid: editingInvoice.amountPaid,
                          balance: editingInvoice.balance
                        });
                        setIsEditingInvoice(false);
                        setEditingInvoice(null);
                        setShowDetails(false);
                        // Refresh the page after a short delay
                        setTimeout(() => {
                          window.location.reload();
                        }, 500);
                      }}
                      className="flex-1"
                    >
                      Update Invoice
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sourcing Request Display */}
            {request && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Request: {request.request_number || request.requestNumber}
                  </CardTitle>
                  <CardDescription>
                    Sourcing request details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Customer:</span>
                      <p>{request.customer_name} ({request.customer_code})</p>
                    </div>
                    <div>
                      <span className="font-medium">Product:</span>
                      <p>{request.product_name || 'Multiple Items'}</p>
                    </div>
                    {request.items && request.items.length > 0 && (
                      <div>
                        <span className="font-medium">Total Items:</span>
                        <p>{request.items.length} item{request.items.length !== 1 ? 's' : ''}</p>
                      </div>
                    )}
                    {!request.items || request.items.length === 0 && (
                      <div>
                        <span className="font-medium">Quantity:</span>
                        <p>{request.quantity} {request.quantity_unit}</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <span className="font-medium text-sm">Current Status:</span>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(request.status)}>
                        {request.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Edit Button */}
                  <div className="pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setEditingRequest(request);
                        setIsEditingRequest(true);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Request Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Inline Edit Sourcing Request Form */}
            {isEditingRequest && editingRequest && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Edit Request: {editingRequest.request_number || editingRequest.requestNumber}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsEditingRequest(false);
                        setEditingRequest(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    You can edit product details but not the quoted prices
                  </CardDescription>
                </CardHeader>
                <CardContent className="max-h-[70vh] overflow-y-auto space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-productName">Product Name</Label>
                    <Input
                      id="edit-productName"
                      value={editingRequest.product_name || editingRequest.productName || ''}
                      onChange={(e) => setEditingRequest({ 
                        ...editingRequest, 
                        product_name: e.target.value,
                        productName: e.target.value 
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-description">Description</Label>
                    <Textarea
                      id="edit-description"
                      value={editingRequest.description || ''}
                      onChange={(e) => setEditingRequest({ ...editingRequest, description: e.target.value })}
                      rows={4}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-quantity">Quantity</Label>
                      <Input
                        id="edit-quantity"
                        type="number"
                        value={editingRequest.quantity || 0}
                        onChange={(e) => setEditingRequest({ ...editingRequest, quantity: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-unit">Unit</Label>
                      <Select
                        value={editingRequest.quantity_unit || editingRequest.quantityUnit || 'pcs'}
                        onValueChange={(value) => setEditingRequest({ 
                          ...editingRequest, 
                          quantity_unit: value,
                          quantityUnit: value 
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pcs">pcs</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="boxes">boxes</SelectItem>
                          <SelectItem value="sets">sets</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-specialRequirements">Special Requirements</Label>
                    <Textarea
                      id="edit-specialRequirements"
                      value={editingRequest.special_requirements || editingRequest.specialRequirements || ''}
                      onChange={(e) => setEditingRequest({ 
                        ...editingRequest, 
                        special_requirements: e.target.value,
                        specialRequirements: e.target.value 
                      })}
                      rows={3}
                    />
                  </div>

                  {/* Show locked prices if quoted */}
                  {editingRequest.status === 'Quoted' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Quoted Prices (Cannot be edited):</strong>
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Price per unit:</span>
                          <span className="font-semibold">${parseFloat(editingRequest.quoted_price_per_unit || editingRequest.quotedPricePerUnit || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Grand Total:</span>
                          <span className="font-semibold">${parseFloat(editingRequest.grand_total || editingRequest.grandTotal || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditingRequest(false);
                        setEditingRequest(null);
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        updateSourcingRequest({
                          request_number: editingRequest.request_number || editingRequest.requestNumber,
                          product_name: editingRequest.product_name || editingRequest.productName,
                          description: editingRequest.description,
                          quantity: editingRequest.quantity,
                          quantity_unit: editingRequest.quantity_unit || editingRequest.quantityUnit,
                          special_requirements: editingRequest.special_requirements || editingRequest.specialRequirements
                        });
                        setIsEditingRequest(false);
                        setEditingRequest(null);
                        setShowDetails(false);
                        // Refresh the page after a short delay
                        setTimeout(() => {
                          window.location.reload();
                        }, 500);
                      }}
                      className="flex-1"
                    >
                      Save Changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Instructions */}
            {!shipment && !invoice && !request && !error && !loading && (
              <Card className="border-dashed">
                <CardContent className="p-6 text-center">
                  <Scan className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <h3 className="font-medium text-gray-900 mb-1">Ready to Scan</h3>
                  <p className="text-sm text-gray-500">
                    Point your camera at a QR code to get started
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Scan shipment, invoice, or sourcing request QR codes
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
